<script setup lang="ts">
// 内容审核容器：按菜单 code 分发到 词库 / 策略 / 日志 三个管理页
// 后端注册 content-review 模块 + 三个菜单后，App.vue 将 kind=content-review 的 activeMenu 注入本组件
import { computed, ref } from 'vue'
import type { ModuleMenuItem } from '../../types'
import {
  CONTENT_REVIEW_LEXICON_MENU_CODE,
  CONTENT_REVIEW_LOGS_MENU_CODE,
  CONTENT_REVIEW_POLICY_MENU_CODE
} from '../../utils/menuArchitecture'
import ContentLexiconManager from './ContentLexiconManager.vue'
import ContentLogManager from './ContentLogManager.vue'
import ContentPolicyManager from './ContentPolicyManager.vue'

const props = defineProps<{
  menu: ModuleMenuItem
  canManage: boolean
}>()

const emit = defineEmits<{
  notice: [message: string]
  error: [message: string]
}>()

type ReviewSection = 'lexicon' | 'policy' | 'logs' | 'unknown'

const section = computed<ReviewSection>(() => {
  const code = props.menu.code
  if (code === CONTENT_REVIEW_LEXICON_MENU_CODE || code.endsWith('.lexicon')) return 'lexicon'
  if (code === CONTENT_REVIEW_POLICY_MENU_CODE || code.endsWith('.policy')) return 'policy'
  if (code === CONTENT_REVIEW_LOGS_MENU_CODE || code.endsWith('.logs')) return 'logs'
  return 'unknown'
})

const lexiconRef = ref<InstanceType<typeof ContentLexiconManager> | null>(null)
const policyRef = ref<InstanceType<typeof ContentPolicyManager> | null>(null)
const logsRef = ref<InstanceType<typeof ContentLogManager> | null>(null)

function refresh() {
  if (section.value === 'lexicon') return lexiconRef.value?.refresh?.()
  if (section.value === 'policy') return policyRef.value?.refresh?.()
  if (section.value === 'logs') return logsRef.value?.refresh?.()
}

defineExpose({ refresh })
</script>

<template>
  <ContentLexiconManager
    v-if="section === 'lexicon'"
    ref="lexiconRef"
    :can-manage="canManage"
    @notice="(message) => emit('notice', message)"
    @error="(message) => emit('error', message)"
  />
  <ContentPolicyManager
    v-else-if="section === 'policy'"
    ref="policyRef"
    :can-manage="canManage"
    @notice="(message) => emit('notice', message)"
    @error="(message) => emit('error', message)"
  />
  <ContentLogManager
    v-else-if="section === 'logs'"
    ref="logsRef"
    :can-manage="canManage"
    @notice="(message) => emit('notice', message)"
    @error="(message) => emit('error', message)"
  />
  <section v-else class="panel span empty-state">
    <p>内容审核模块菜单未识别，请检查后端菜单注册（content-review.lexicon / .policy / .logs）。</p>
  </section>
</template>
